import Markdoc, {
  type Config,
  type Node,
  type RenderableTreeNode,
  type Schema,
  Tag,
} from "@markdoc/markdoc";
import { parse as parseYaml } from "yaml";
import { z } from "zod";

import type { ElementDocs, ElementPropDoc, ElementSeam } from "./elementDocs";
import { EMBED_TAG_ATTRIBUTES } from "./embedParams.gen";

/**
 * The tokenizer every parse in this package and its callers goes through.
 *
 * `allowComments` is the whole reason it is stated rather than left to the
 * default. Markdoc's default tokenizer has no notion of an HTML comment, so
 * `<!-- … -->` arrives as a markdown-it html token and is emitted as TEXT —
 * which renders a comment on the page, in a font, as prose. That is not
 * hypothetical: the snippet type-checker's opt-out marker is an HTML comment
 * above a fence, and every one of them was printed to readers as a paragraph of
 * build-tool apology. With this on they become `comment` tokens and are dropped
 * before the AST exists, which is what an author writing one means by it.
 *
 * Module-level: it is rebuilt per page otherwise, and it holds no per-parse
 * state.
 */
const tokenizer = new Markdoc.Tokenizer({ allowComments: true });

/**
 * parseMarkdoc is the one entry to Markdoc's parser in this codebase.
 *
 * Published because a caller assembling partials parses them itself, and a
 * partial parsed through the bare `Markdoc.parse` would render its comments
 * while the page including it did not. One door means the two cannot disagree;
 * `config.test.ts` pins that a comment survives neither route.
 */
export const parseMarkdoc = (source: string): Node => Markdoc.parse(tokenizer.tokenize(source));

/**
 * slugify turns heading text into a deterministic anchor id: lowercased,
 * trimmed, with every run of non-alphanumeric characters collapsed to a single
 * hyphen and leading/trailing hyphens removed. ToC entries and heading anchors
 * share these ids so in-page links resolve.
 */
export const slugify = (text: string): string =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * collectText concatenates the textual content of a node subtree. Fenced and
 * inline-code nodes contribute their raw `content` attribute and their children
 * are skipped (a fence duplicates its text as a child), so code bodies are not
 * counted twice; every other node contributes the joined text of its children.
 */
const collectText = (node: Node): string => {
  if (node.type === "text") {
    const content = node.attributes.content;

    return typeof content === "string" ? content : "";
  }

  if (node.type === "fence" || node.type === "code") {
    const content = node.attributes.content;

    return typeof content === "string" ? content : "";
  }

  return node.children.map(collectText).join("");
};

/**
 * headingNode overrides the default heading so each heading renders to the
 * `Heading` component with a deterministic slug `id` and its `level`. getToc and
 * the rendered anchors read the same id.
 */
const headingNode: Schema = {
  attributes: {
    id: { type: String },
    level: { required: true, type: Number },
  },
  render: "Heading",
  transform(node, config) {
    const attributes = node.transformAttributes(config);
    const children = node.transformChildren(config);
    const level = typeof node.attributes.level === "number" ? node.attributes.level : 1;
    const id = slugify(collectText(node));

    return new Tag("Heading", { ...attributes, id, level }, children);
  },
};

/**
 * paragraphNode renders body paragraphs through the `Paragraph` component
 * (Mantine `Text`).
 */
const paragraphNode: Schema = {
  render: "Paragraph",
};

/**
 * fenceNode renders fenced code blocks through the `CodeBlock` component, which
 * highlights `content` for the given `language`.
 */
const fenceNode: Schema = {
  attributes: {
    content: { render: true, type: String },
    language: { type: String },
  },
  render: "CodeBlock",
};

/**
 * codeNode renders inline code through the `InlineCode` component (Mantine
 * `Code`).
 */
const codeNode: Schema = {
  attributes: {
    content: { render: true, type: String },
  },
  render: "InlineCode",
};

/**
 * linkNode renders links through the `Link` component, which distinguishes
 * internal from external targets.
 */
const linkNode: Schema = {
  attributes: {
    href: { required: true, type: String },
    title: { type: String },
  },
  render: "Link",
};

/**
 * imageNode renders images through the responsive `Image` component.
 */
const imageNode: Schema = {
  attributes: {
    alt: { type: String },
    src: { required: true, type: String },
    title: { type: String },
  },
  render: "Image",
};

/**
 * tableNode and its row/cell relatives render through the `Table` component
 * family.
 */
const tableNode: Schema = { render: "Table" };
const theadNode: Schema = { render: "TableThead" };
const tbodyNode: Schema = { render: "TableTbody" };
const trNode: Schema = { render: "TableTr" };
const thNode: Schema = { render: "TableTh" };
const tdNode: Schema = { render: "TableTd" };

/**
 * alertTag is the schema for the {% alert %} tag: a callout with a variant and
 * an optional title. Tag and attribute schemas live in this render-free entry;
 * the JSX that renders them lives in @mountayaapp/markdoc/renderers.
 */
const alertTag: Schema = {
  attributes: {
    title: { type: String },
    variant: {
      default: "info",
      matches: ["info", "note", "tip", "warning", "danger", "success"],
      type: String,
    },
  },
  render: "Alert",
};

/**
 * accordionItemValue derives the unique `value` an {% accordion-item %} is keyed
 * by within its parent {% accordion %}. Mantine toggles items by `value`, so two
 * items sharing a label must still get distinct values — the slugified label is
 * suffixed with the item's ordinal among its siblings (falling back to
 * `item-<index>` when the label slugifies to nothing). Only the parent transform
 * knows sibling order, so it is the one that assigns this.
 */
const accordionItemValue = (item: Tag, index: number): string => {
  const label =
    typeof item.attributes.label === "string"
      ? item.attributes.label
      : typeof item.attributes.title === "string"
        ? item.attributes.title
        : "";
  const slug = slugify(label);

  return slug !== "" ? `${slug}-${index}` : `item-${index}`;
};

/**
 * accordionTag is the schema for the {% accordion %} tag, a container of
 * {% accordion-item %} sections. The transform stamps each child item with a
 * unique `value` derived from its label and its ordinal, so repeated labels key
 * distinct sections instead of toggling together.
 */
const accordionTag: Schema = {
  render: "Accordion",
  transform(node, config) {
    const attributes = node.transformAttributes(config);
    const children = node.transformChildren(config);

    let itemIndex = 0;
    for (const child of children) {
      if (Tag.isTag(child) && child.name === "AccordionItem") {
        child.attributes.value = accordionItemValue(child, itemIndex);
        itemIndex += 1;
      }
    }

    return new Tag("Accordion", attributes, children);
  },
};

/**
 * accordionItemTag is the schema for one {% accordion-item %}: a labelled,
 * collapsible section. Both `label` and `title` set the control label; the parent
 * {% accordion %} transform fills in the unique `value` the item is keyed by.
 */
const accordionItemTag: Schema = {
  attributes: {
    label: { type: String },
    title: { type: String },
    value: { type: String },
  },
  render: "AccordionItem",
};

/**
 * CodeTab is one snippet inside a {% code-group %}: its tab label, optional
 * highlight language, and raw code content.
 */
export interface CodeTab {
  content: string;
  label: string;
  language?: string;
  value: string;
}

/**
 * readCodeBlock reads one {% code-block %} node into a CodeTab. The body text is
 * the raw code; `label` or `title` names the tab; a slugified value keys the
 * tab. The index disambiguates otherwise-identical labels.
 */
const readCodeBlock = (node: Node, index: number): CodeTab => {
  const label =
    typeof node.attributes.label === "string"
      ? node.attributes.label
      : typeof node.attributes.title === "string"
        ? node.attributes.title
        : `Tab ${index + 1}`;
  const language =
    typeof node.attributes.language === "string" ? node.attributes.language : undefined;
  const slug = slugify(label);
  const value = slug !== "" ? `${slug}-${index}` : `tab-${index}`;

  return { content: collectText(node), label, language, value };
};

/**
 * codeGroupTag is the schema for the {% code-group %} tag, a tabbed container of
 * {% code-block %} snippets. The transform folds each child code block into a
 * `tabs` attribute so the renderer builds the tab strip directly from data.
 */
const codeGroupTag: Schema = {
  render: "CodeGroup",
  transform(node) {
    const tabs = node.children
      .filter((child) => child.type === "tag" && child.tag === "code-block")
      .map(readCodeBlock);

    return new Tag("CodeGroup", { tabs }, []);
  },
};

/**
 * codeBlockTag is the schema for one {% code-block %}. Inside a {% code-group %}
 * its data is folded into the parent's tab strip; standalone it renders through
 * the `CodeBlock` component. Its body is captured as the highlighted `content`,
 * and `label`/`title` name it.
 */
const codeBlockTag: Schema = {
  attributes: {
    label: { type: String },
    language: { type: String },
    title: { type: String },
  },
  render: "CodeBlock",
  transform(node, config) {
    const attributes = node.transformAttributes(config);
    const content = collectText(node);

    return new Tag("CodeBlock", { ...attributes, content }, []);
  },
};

/**
 * buttonTag is the schema for the {% button %} tag: a call-to-action link
 * styled as a button. `href` is required; `variant` selects the Mantine button
 * style; `icon` names an optional leading Nucleo icon; the body is the label.
 * The renderer detects external `http(s)` targets and opens them safely.
 */
const buttonTag: Schema = {
  attributes: {
    href: { required: true, type: String },
    icon: { type: String },
    variant: {
      default: "filled",
      matches: ["filled", "outline", "light", "default"],
      type: String,
    },
  },
  render: "Button",
};

/**
 * cardGroupTag is the schema for the {% card-group %} tag, a responsive grid of
 * {% card %} blocks. `cols` sets the column count at the widest breakpoint
 * (default two).
 */
const cardGroupTag: Schema = {
  attributes: {
    cols: { default: 2, type: Number },
  },
  render: "CardGroup",
};

/**
 * cardTag is the schema for one {% card %}: a linkable tile with a `title`, an
 * optional leading `icon` (Nucleo name), an optional `href` that turns the whole
 * card into an anchor, and a body that is the card description.
 */
const cardTag: Schema = {
  attributes: {
    href: { type: String },
    icon: { type: String },
    title: { type: String },
  },
  render: "Card",
};

/**
 * stepsTag is the schema for the {% steps %} tag, a numbered vertical sequence
 * of {% step %} blocks. The transform stamps each step child with its ordinal
 * (only the parent knows sibling order) so the renderer can show the step
 * number without re-counting.
 */
const stepsTag: Schema = {
  render: "Steps",
  transform(node, config) {
    const attributes = node.transformAttributes(config);
    const children = node.transformChildren(config);

    let stepIndex = 0;
    for (const child of children) {
      if (Tag.isTag(child) && child.name === "Step") {
        child.attributes.ordinal = stepIndex + 1;
        stepIndex += 1;
      }
    }

    return new Tag("Steps", attributes, children);
  },
};

/**
 * stepTag is the schema for one {% step %}: a titled stage in a {% steps %}
 * sequence. `title` names the stage; the parent {% steps %} transform fills in
 * the `ordinal` shown as the step number.
 */
const stepTag: Schema = {
  attributes: {
    ordinal: { type: Number },
    title: { type: String },
  },
  render: "Step",
};

/**
 * TabItem is one entry in a {% tabs %} strip: the tab's label and the unique
 * value that pairs its control with its panel. The parent {% tabs %} transform
 * folds the child {% tab %} list into this shape so the renderer builds the
 * strip from data while each {% tab %} still renders its own rich panel body.
 */
export interface TabItem {
  label: string;
  value: string;
}

/**
 * tabItemValue derives the unique `value` a {% tab %} is keyed by within its
 * parent {% tabs %}. Mantine pairs a control with its panel by `value`, so two
 * tabs sharing a label must still get distinct values — the slugified label is
 * suffixed with the tab's ordinal among its siblings (falling back to
 * `tab-<index>` when the label slugifies to nothing). Only the parent transform
 * knows sibling order, so it is the one that assigns this.
 */
const tabItemValue = (tab: Tag, index: number): string => {
  const label = typeof tab.attributes.label === "string" ? tab.attributes.label : "";
  const slug = slugify(label);

  return slug !== "" ? `${slug}-${index}` : `tab-${index}`;
};

/**
 * tabsTag is the schema for the {% tabs %} tag, a tabbed container of {% tab %}
 * blocks with rich (non-code) bodies. The transform does both: it stamps each
 * tab child a unique `value` (so its panel pairs with its control) and folds the
 * child {label, value} list into a `tabs` attribute (so the renderer builds the
 * tab strip from data). The children stay in the tree because each {% tab %}
 * renders its own panel body.
 */
const tabsTag: Schema = {
  attributes: {
    tabs: { type: Array },
  },
  render: "Tabs",
  transform(node, config) {
    const children = node.transformChildren(config);

    let tabIndex = 0;
    const tabs: TabItem[] = [];
    for (const child of children) {
      if (Tag.isTag(child) && child.name === "Tab") {
        const value = tabItemValue(child, tabIndex);
        const label = typeof child.attributes.label === "string" ? child.attributes.label : value;
        child.attributes.value = value;
        tabs.push({ label, value });
        tabIndex += 1;
      }
    }

    return new Tag("Tabs", { tabs }, children);
  },
};

/**
 * tabTag is the schema for one {% tab %}: a labelled panel with arbitrary rich
 * children. `label` names the control; the parent {% tabs %} transform fills in
 * the unique `value` the panel is keyed by.
 */
const tabTag: Schema = {
  attributes: {
    label: { type: String },
    value: { type: String },
  },
  render: "Tab",
};

/**
 * badgeTag is the schema for the {% badge %} tag: an inline status pill. `color`
 * and `variant` style it; the body (or `label`) is the text.
 */
const badgeTag: Schema = {
  attributes: {
    color: { type: String },
    label: { type: String },
    variant: { type: String },
  },
  render: "Badge",
};

/**
 * propertiesTag is the schema for the {% properties %} tag, a container for the
 * {% property %} rows that describe an API object's fields.
 */
const propertiesTag: Schema = {
  render: "Properties",
};

/**
 * propertyTag is the schema for one {% property %}: a single API field. `name`
 * and `type` identify it, `required` flags a mandatory field, `default` shows a
 * fallback value, and the body is the field description.
 */
const propertyTag: Schema = {
  attributes: {
    default: { type: String },
    name: { required: true, type: String },
    required: { default: false, type: Boolean },
    type: { type: String },
  },
  render: "Property",
};

/**
 * resolveElementProps answers a {% props %} reference with the rows it should draw.
 * `of="ItineraryProfile"` names an element and `of="ItineraryProfile.XAxis"` one of
 * its parts, which is the same spelling a reader uses to place them, so the tag and
 * the JSX it documents are reached by one name.
 */
const resolveElementProps = (
  reference: string,
  elements: ElementDocs,
): { readonly domAttributes?: string; readonly props: readonly ElementPropDoc[] } | null => {
  const [name = "", part] = reference.split(".");
  const element = elements[name];

  if (element === undefined) {
    return null;
  }

  if (part === undefined) {
    return { domAttributes: element.domAttributes, props: element.props };
  }

  const resolved = element.parts.find((entry) => entry.name === part);

  return resolved === undefined
    ? null
    : { domAttributes: resolved.domAttributes, props: resolved.props };
};

/**
 * renderDescription turns a prop's JSDoc into renderable children. A description is
 * authored Markdown in the package's own source, so it is parsed rather than passed
 * through as text: a backtick or a bold run reads the way it would in a hand-written
 * row. Markdoc wraps a parsed document in a container tag, whose children are the
 * blocks wanted here; a description that produced a bare scalar is used as-is.
 */
const renderDescription = (description: string, config: Config): RenderableTreeNode[] => {
  if (description.trim() === "") {
    return [];
  }

  const rendered = Markdoc.transform(parseMarkdoc(description), config);

  return Tag.isTag(rendered) ? rendered.children : [rendered];
};

/**
 * propsTag is the schema for {% props of="ItineraryProfile" %}: the props table for
 * an element or one of its parts, read from the generated catalogue.
 *
 * It transforms into the same Properties/Property tags a hand-written table uses,
 * rather than rendering through a component of its own. That is deliberate on three
 * counts: the reduced-Markdown twin the `llms.txt` surface is built from already
 * serializes those two names, so a generated table survives into it without another
 * case to keep in step; the table of contents and the search index read the
 * transformed tree, so generated rows are found the way authored ones are; and one
 * visual language means a reader cannot tell which tables were written by hand,
 * which is the whole point of generating them.
 *
 * `only=` and `omit=` split the shared contracts — the `on*` handlers and the
 * className/style pair — from an element's own props, so a page can carry its element's
 * table and point at one shared page for the rest. `"seams"` names both contracts at
 * once; `"handlers"` and `"style"` name one each, because the two answer different
 * questions and a page may want them on different tabs: a handler is behaviour a
 * consumer takes over, where className/style is how the element is painted.
 */
const propsTag: Schema = {
  attributes: {
    of: { required: true, type: String },
    omit: { matches: ["handlers", "seams", "style"], type: String },
    only: { matches: ["handlers", "seams", "style"], type: String },
  },
  render: "Properties",
  transform(node, config) {
    const attributes = node.transformAttributes(config);
    const reference = String(attributes.of ?? "");
    const elements = (config.variables?.elements ?? {}) as ElementDocs;
    const resolved = resolveElementProps(reference, elements);

    // An unknown reference renders as a visible error rather than an empty table.
    // A silently empty table reads as "this element takes no props", which is a
    // stronger and more wrong claim than saying the name did not resolve.
    if (resolved === null) {
      return new Tag("Alert", { title: "Unknown element", variant: "danger" }, [
        `No generated documentation for "${reference}".`,
      ]);
    }

    // A filter names the set of seams it is about, and `only`/`omit` are its two
    // directions over that set. Written once here rather than as a pair of conditions,
    // so the two directions cannot drift apart as a third seam is added.
    const selects = (selector: string, seam: ElementSeam | undefined): boolean => {
      if (selector === "seams") {
        return seam !== undefined;
      }

      return selector === "handlers" ? seam === "handler" : seam === "style";
    };

    const rows = resolved.props
      .filter((prop) => {
        if (typeof attributes.only === "string") {
          return selects(attributes.only, prop.seam);
        }

        return typeof attributes.omit === "string" ? !selects(attributes.omit, prop.seam) : true;
      })
      .map(
        (prop) =>
          new Tag(
            "Property",
            {
              ...(prop.defaultValue === undefined ? {} : { default: prop.defaultValue }),
              name: prop.name,
              required: prop.required,
              type: prop.type,
            },
            renderDescription(prop.description, config),
          ),
      );

    // An element that spreads a DOM element's attributes accepts hundreds more than these,
    // and the generator deliberately does not enumerate them. Saying so is the difference
    // between a short table and a table that is WRONG about the element's surface.
    return new Tag(
      "Properties",
      resolved.domAttributes === undefined ? {} : { domAttributes: resolved.domAttributes },
      rows,
    );
  },
};

/**
 * iframeTag is the schema for the {% iframe %} tag: a responsive embedded frame.
 * `src` is required; `title`, `height`, and `allow` are passed through. The
 * renderer lazy-loads the frame and is server-rendering safe.
 */
const iframeTag: Schema = {
  attributes: {
    allow: { type: String },
    height: { default: 480, type: Number },
    src: { required: true, type: String },
    title: { type: String },
  },
  render: "Iframe",
};

/**
 * heroTag is the schema for the {% hero %} tag: a prominent landing header with
 * a `title`, an optional `subtitle`, and a body that holds the call-to-action
 * row.
 */
const heroTag: Schema = {
  attributes: {
    subtitle: { type: String },
    title: { type: String },
  },
  render: "Hero",
};

/**
 * mapEmbeddingTag is the schema for the {% map-embedding %} tag: a live Map
 * Embedding iframe. Its attributes mirror the embed search params so a doc page
 * can pin a map's credentials, content selection, terrain, overlay, preferences,
 * and UI toggles. The renderer that builds the embed URL is injected by the docs
 * app, so this tag carries no transform.
 */
const mapEmbeddingTag: Schema = {
  attributes: {
    ...EMBED_TAG_ATTRIBUTES,
    // The frame's own props, which configure the iframe rather than the embed, plus
    // `view` — the studio reads its opening camera from the URL hash, so it never
    // appears among the search params the generated map is read from.
    height: { type: Number },
    title: { type: String },
    view: { type: String },
  },
  render: "MapEmbedding",
};

/**
 * elementTag is the schema for {% element %}: one real element from the published
 * package, running on the page.
 *
 * `name` selects a demo from the docs app's own registry rather than accepting free-form
 * props, so the composition stays TypeScript that `tsc` checks against the elements. A
 * tag that took arbitrary props would be a second, worse JSX.
 *
 * This is deliberately NOT {% map-embedding %}. That tag frames the hosted studio, which
 * is the other product; using it to illustrate the components would teach the wrong
 * thing at the exact moment a reader is choosing between them. The heavy renderer is
 * injected by the docs app, so this tag carries no transform.
 */
const elementTag: Schema = {
  attributes: {
    name: { required: true, type: String },
  },
  render: "Element",
};

/**
 * graphiqlTag is the schema for the {% graphiql %} tag: an interactive GraphQL
 * explorer. `schema` selects which static SDL to load (default the Data API
 * schema); `query` seeds the editor. The heavy renderer is injected by the docs
 * app, so this tag carries no transform.
 */
const graphiqlTag: Schema = {
  attributes: {
    query: { type: String },
    schema: { default: "graphql/data", type: String },
  },
  render: "GraphiQL",
};

/**
 * redocTag is the schema for the {% redoc %} tag: an embedded OpenAPI reference.
 * `spec` selects which bundled specification to render. `title` and `lead` draw a
 * docs-styled header band above the reference so the page opens with a title and
 * a one-line overview like every other content page (Redoc hides its own info
 * block). The heavy renderer is injected by the docs app, so this tag carries no
 * transform.
 */
const redocTag: Schema = {
  attributes: {
    lead: { type: String },
    spec: {
      default: "tiles",
      matches: ["tiles", "api"],
      type: String,
    },
    title: { type: String },
  },
  render: "Redoc",
};

/**
 * changelogTag is the schema for the {% changelog %} tag: the platform's release
 * history. It takes no attributes and carries no transform; the renderer that draws
 * it is injected by the docs app, like the other heavy tags.
 */
const changelogTag: Schema = {
  render: "Changelog",
};

/**
 * markdocConfig is the render-free Markdoc configuration: the sanctioned node
 * overrides and tag set for documentation content.
 */
export const markdocConfig: Config = {
  nodes: {
    code: codeNode,
    fence: fenceNode,
    heading: headingNode,
    image: imageNode,
    link: linkNode,
    paragraph: paragraphNode,
    table: tableNode,
    tbody: tbodyNode,
    td: tdNode,
    th: thNode,
    thead: theadNode,
    tr: trNode,
  },
  tags: {
    accordion: accordionTag,
    "accordion-item": accordionItemTag,
    alert: alertTag,
    badge: badgeTag,
    button: buttonTag,
    card: cardTag,
    "card-group": cardGroupTag,
    changelog: changelogTag,
    "code-block": codeBlockTag,
    "code-group": codeGroupTag,
    element: elementTag,
    graphiql: graphiqlTag,
    hero: heroTag,
    iframe: iframeTag,
    "map-embedding": mapEmbeddingTag,
    properties: propertiesTag,
    property: propertyTag,
    props: propsTag,
    redoc: redocTag,
    step: stepTag,
    steps: stepsTag,
    tab: tabTag,
    tabs: tabsTag,
  },
};

/**
 * frontmatterSchema validates a document's YAML frontmatter. Every field is
 * lenient — a sparse or empty frontmatter yields defaults rather than throwing,
 * so partially-authored pages still render. `toc` defaults to true; a page sets
 * it false to drop the in-page table-of-contents rail and reclaim that width
 * (used by the full-width interactive pages: the playground and the OpenAPI
 * reference). `full` defaults to false; a page sets it true to drop the content
 * container and its padding entirely so an embedded explorer fills the viewport
 * edge-to-edge below the header (the playground and the Tile API reference).
 */
export const frontmatterSchema = z.object({
  description: z.string().optional().catch(undefined),
  full: z.boolean().catch(false),
  group: z.string().optional().catch(undefined),
  order: z.number().optional().catch(undefined),
  title: z.string().catch("Untitled"),
  toc: z.boolean().catch(true),
});

/**
 * Frontmatter is the validated shape of a document's frontmatter block.
 */
export type Frontmatter = z.infer<typeof frontmatterSchema>;

/**
 * TocEntry is one heading in a document's table of contents.
 */
export interface TocEntry {
  id: string;
  level: number;
  title: string;
}

/**
 * collectTagText concatenates the textual content of a rendered tree, reading
 * string leaves and recursing into tag children. It backs ToC titles.
 */
const collectTagText = (node: RenderableTreeNode): string => {
  if (typeof node === "string") {
    return node;
  }

  if (Tag.isTag(node)) {
    return node.children.map(collectTagText).join("");
  }

  return "";
};

/**
 * dedupeHeadingIds rewrites colliding heading anchor ids in place so each is
 * unique within the document. Heading ids derive from heading text, so two
 * headings reading the same words would otherwise share a DOM id (an ambiguous
 * anchor) and a duplicate ToC key. The first occurrence of an id keeps it;
 * each later collision is suffixed `-2`, `-3`, … against the next free slot.
 * A heading whose text carries no slug characters has an empty id and is left
 * alone: there is no anchor to disambiguate, and suffixing one would mint an id
 * slugify itself could never produce, then list that heading in the ToC while
 * its identically-worded twin stayed out. Markdoc `Tag` attributes are mutable,
 * so the rewrite mutates the tree the ToC then reads, keeping anchors and ToC
 * entries in lockstep.
 */
const dedupeHeadingIds = (tree: RenderableTreeNode): void => {
  const seen = new Map<string, number>();

  const visit = (node: RenderableTreeNode): void => {
    if (!Tag.isTag(node)) {
      return;
    }

    if (
      node.name === "Heading" &&
      typeof node.attributes.id === "string" &&
      node.attributes.id !== ""
    ) {
      const baseId = node.attributes.id;
      const priorCount = seen.get(baseId) ?? 0;

      if (priorCount === 0) {
        seen.set(baseId, 1);
      } else {
        let suffix = priorCount + 1;
        let candidate = `${baseId}-${suffix}`;
        while (seen.has(candidate)) {
          suffix += 1;
          candidate = `${baseId}-${suffix}`;
        }

        seen.set(baseId, suffix);
        seen.set(candidate, 1);
        node.attributes.id = candidate;
      }
    }

    for (const child of node.children) {
      visit(child);
    }
  };

  visit(tree);
};

/**
 * getToc collects the table of contents from a rendered tree: every `Heading`
 * of level 2 or 3 (the h1 page title is skipped) with its slug `id`, `level`,
 * and text `title`.
 */
export const getToc = (tree: RenderableTreeNode): TocEntry[] => {
  const entries: TocEntry[] = [];

  const visit = (node: RenderableTreeNode): void => {
    if (!Tag.isTag(node)) {
      return;
    }

    if (node.name === "Heading") {
      const level = typeof node.attributes.level === "number" ? node.attributes.level : 0;
      const id = typeof node.attributes.id === "string" ? node.attributes.id : "";

      if (level >= 2 && level <= 3 && id !== "") {
        entries.push({ id, level, title: collectTagText(node).trim() });
      }
    }

    for (const child of node.children) {
      visit(child);
    }
  };

  visit(tree);

  return entries;
};

/**
 * ParsedDocument is the result of parsing a Markdoc source: the renderable tree,
 * its validated frontmatter, and its table of contents.
 */
export interface ParsedDocument {
  frontmatter: Frontmatter;
  toc: TocEntry[];
  tree: RenderableTreeNode;
}

/**
 * ParseDocumentOptions tunes a parseDocument call. `partials` supplies the
 * parsed ASTs that {% partial file="…" %} expands by name — the docs app globs
 * its `content/_partials/**` and passes them here so shared enum/parameter
 * tables stay defined once. `elements` supplies the generated element catalogue
 * the {% props %} tag reads, so this package renders a props table without ever
 * naming an SDK: it learns a shape through this seam, never a source.
 */
export interface ParseDocumentOptions {
  elements?: ElementDocs;
  partials?: Record<string, Node>;
}

/**
 * parseDocument turns Markdoc source into a renderable tree, its validated
 * frontmatter, and its table of contents. Render-free — the docs app feeds the
 * tree to the /renderers subpath. Frontmatter is read from the raw YAML block
 * Markdoc exposes, parsed, then validated leniently so a sparse block never
 * throws. Optional `partials` are merged into the transform config so
 * {% partial file="…" %} references resolve.
 */
export const parseDocument = (source: string, options?: ParseDocumentOptions): ParsedDocument => {
  const ast = parseMarkdoc(source);
  const rawFrontmatter = ast.attributes.frontmatter;
  const parsedFrontmatter: unknown =
    typeof rawFrontmatter === "string" && rawFrontmatter.trim() !== ""
      ? parseYaml(rawFrontmatter)
      : {};
  const frontmatter = frontmatterSchema.parse(parsedFrontmatter ?? {});

  const config: Config = {
    ...markdocConfig,
    ...(options?.partials ? { partials: options.partials } : {}),
    variables: { elements: options?.elements ?? {} },
  };
  const tree = Markdoc.transform(ast, config);
  dedupeHeadingIds(tree);
  const toc = getToc(tree);

  return { frontmatter, toc, tree };
};
